/**
 * Chips der aktiven Filter (Board „Verlauf“): je Filtergruppe einer mit ✕, ein Zeitraum ist ein Chip.
 * Ihre Zahl ist „Filter · n“; das Apartment zählt nicht mit, es hat ein eigenes Feld. Filter, die der
 * Server in der gezeigten Ansicht nicht anwendet (Zustellung nur bei Übertragungen, Art nur bei
 * gültigen Werten), sagen das und zählen nicht mit.
 */
import { historyDateTime, historyShortDate } from './historyFormat';
import { EXPOSURE_KIND, OUTCOME, PUBLICATION_FILTER_LABEL, TRIGGER } from './historyText';
import type { DynamicPricingHistoryView } from '@/shared/api';
import { listOf, type DynamicPricingHistorySearch } from './historyUrlState';

type Key = keyof DynamicPricingHistorySearch;

export interface HistoryChip {
  label: string;
  /** Diese Parameter fallen beim ✕ weg. */
  clears: Key[];
  /** Wirkt nur in dieser Ansicht, nicht in der gezeigten. */
  onlyIn?: string;
}

const ONLY: Partial<Record<Key, [DynamicPricingHistoryView, string]>> = {
  publicationCreatedFrom: ['publications', 'Übertragungen'],
  publicationStatus: ['publications', 'Übertragungen'],
  exposureConfirmedFrom: ['exposures', 'Gültige Werte'],
  exposureKind: ['exposures', 'Gültige Werte'],
};

const range = (from: string | undefined, to: string | undefined, format: (v: string) => string) =>
  from && to
    ? `${format(from)} – ${format(to)}`
    : from
      ? `ab ${format(from)}`
      : `bis ${format(to!)}`;

export function historyChips(
  search: DynamicPricingHistorySearch,
  timeZone: string,
  view?: DynamicPricingHistoryView,
): HistoryChip[] {
  const chips: HistoryChip[] = [];
  const onlyIn = (key: Key) => {
    const only = ONLY[key];
    return only && view && only[0] !== view ? { onlyIn: only[1] } : {};
  };
  const time = (value: string) => historyDateTime(value, timeZone);
  const span = (name: string, from: Key, to: Key, format: (value: string) => string) => {
    if (search[from] || search[to]) {
      chips.push({
        label: `${name} ${range(search[from], search[to], format)}`,
        clears: [from, to],
        ...onlyIn(from),
      });
    }
  };
  const many = (name: string, key: Key, labels: Record<string, string>) => {
    const values = listOf(search[key]);
    if (values.length) {
      chips.push({
        label: `${name}: ${values.map((v) => labels[v] ?? v).join(', ')}`,
        clears: [key],
        ...onlyIn(key),
      });
    }
  };
  span('Aufenthalt', 'stayFrom', 'stayTo', historyShortDate);
  span('Berechnet', 'decisionFrom', 'decisionTo', time);
  span('Eingereicht', 'publicationCreatedFrom', 'publicationCreatedTo', time);
  span('Bestätigt', 'exposureConfirmedFrom', 'exposureConfirmedTo', time);
  many('Auslöser', 'trigger', TRIGGER);
  many(
    'Ergebnis',
    'outcome',
    Object.fromEntries(Object.entries(OUTCOME).map(([key, value]) => [key, value.label])),
  );
  many('Zustellung', 'publicationStatus', PUBLICATION_FILTER_LABEL);
  many('Art', 'exposureKind', EXPOSURE_KIND);
  if (search.configurationVersion) {
    chips.push({ label: 'Einstellungs-Version', clears: ['configurationVersion'] });
  }
  if (search.profileVersion) chips.push({ label: 'Strategie-Version', clears: ['profileVersion'] });
  return chips;
}
